import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import { WithLoader } from 'Components/WithLoader'
import DeviceDrawer from 'Services/ServiceProviderManagement/Features/Device/Components/DeviceDrawer'
import DeviceList from 'Services/ServiceProviderManagement/Features/Device/Components/DeviceList/DeviceList'
import useDevicesWithParams from 'Services/ServiceProviderManagement/Features/Device/Hooks'
import {
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import AddIcon from '@mui/icons-material/Add'
import { Box, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

export const DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY = 'deviceDetailsDrawerTab'

export default function DeviceListPage() {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/devices`
  const navigate = useNavigate()

  const {
    devices,
    isFetching: isDevicesFetching,
    totalResults,
    params,
    setParams,
    selectedDeviceId,
    setSelectedDeviceId
  } = useDevicesWithParams()

  const [searchText, setSearchText] = useState(params.name ?? '')
  const [searchParams, setSearchParams] = useSearchParams()
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedDeviceId)

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Devices`
    }
  ]

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleDrawerClose = () => {
    setSelectedDeviceId('')
    setIsDrawerOpen(false)
    searchParams.delete(DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY)
    setSearchParams(searchParams)
  }

  const handleRowClick = (id: string) => {
    setSelectedDeviceId(id)
    setIsDrawerOpen(true)
  }

  const isLoading = isDevicesFetching

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Devices`}</Typography>
        <Box sx={{ mt: 4 }}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new device`}
            </CustomButton>
            <ListSearch
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
              handleSearch={handleSearch}
              placeholder={t`Search by name or code`}
            />
          </ListActions>
          <Box sx={{ pt: 1 }}>
            <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
              <DeviceList
                rows={devices}
                isLoading={isLoading}
                params={params}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={totalResults}
                page={
                  params.start_index
                    ? Number.parseInt(params.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                onRowClick={handleRowClick}
                selectedDeviceId={selectedDeviceId}
              />
            </WithLoader>
          </Box>
        </Box>
        <DeviceDrawer
          open={isDrawerOpen}
          handleClose={handleDrawerClose}
          selectedDeviceId={selectedDeviceId}
        />
      </Box>
    </Box>
  )
}
